import { useMemo, useState } from "react";
import type { Dayjs } from "dayjs";

import { DatePicker, PillTabs } from "../../../shared";
import { DATE_RANGE } from "../../../../utils/Constants";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { getAdminNow } from "../../../../utils/dateService";
import {
  useGetUserPromotions,
  useGetUserPromotionsKpis,
  useGetUserReferrals,
  useGetUserReferralsKpis,
} from "../../../../queries/user-manager/useUserPromotions";
import {
  EMPTY_USER_PROMOTIONS_KPIS,
  EMPTY_USER_REFERRALS_KPIS,
} from "./userPromotionsTypes";
import ReferralsTab from "./ReferralsTab/ReferralsTab";
import UserPromotionsTab from "./UserPromotionsTab/UserPromotionsTab";
import styles from "./UserPromotions.module.css";

type PromotionsView = "promotions" | "referrals";

type UserPromotionsProps = {
  userId?: string;
  currencySymbol?: string;
};

const getInitialDateRange = (): [Dayjs, Dayjs] => [
  getAdminNow().subtract(6, "day").startOf("day"),
  getAdminNow().endOf("day"),
];

function UserPromotions({
  userId,
  currencySymbol,
}: UserPromotionsProps) {
  const [view, setView] = useState<PromotionsView>("promotions");
  const [dates, setDates] = useState<[Dayjs, Dayjs]>(getInitialDateRange);

  const queryParams = useMemo(() => {
    if (!userId) return null;
    return {
      userId,
      start_date: dates[0].format("YYYY-MM-DD"),
      end_date: dates[1].format("YYYY-MM-DD"),
    };
  }, [userId, dates]);

  const isPromotionsView = view === "promotions";
  const promotionsKpisQuery = useGetUserPromotionsKpis(
    queryParams,
    isPromotionsView,
  );
  const promotionsQuery = useGetUserPromotions(queryParams, isPromotionsView);
  const referralsKpisQuery = useGetUserReferralsKpis(
    queryParams,
    !isPromotionsView,
  );
  const referralsQuery = useGetUserReferrals(queryParams, !isPromotionsView);

  const activeQueries = isPromotionsView
    ? [promotionsKpisQuery, promotionsQuery]
    : [referralsKpisQuery, referralsQuery];
  const isLoading = activeQueries.some((query) => query.isFetching);
  const isError = activeQueries.some((query) => query.isError);

  const tabs = [
    {
      id: "promotions" as const,
      label: AppStrings.PAM_USER_PROMOTIONS_TAB,
      elementId: "user-promotions-tab",
      ariaControls: "user-promotions-panel",
    },
    {
      id: "referrals" as const,
      label: AppStrings.PAM_USER_REFERRALS_TAB,
      elementId: "user-referrals-tab",
      ariaControls: "user-referrals-panel",
    },
  ];

  const dateFilter = (
    <DatePicker
      dates={dates}
      setDates={setDates}
      getData={() => undefined}
      showPresets
      startRange={DATE_RANGE.LAST_7_DAYS}
    />
  );

  return (
    <section className={styles.container} aria-label={AppStrings.PAM_PROMOTIONS}>
      <PillTabs
        items={tabs}
        value={view}
        onChange={setView}
        ariaLabel={AppStrings.PAM_USER_PROMOTIONS_SECTIONS}
        className={styles.subTabs}
        data-testid="user-promotions-tabs"
      />

      {isError ? (
        <div className={styles.state} role="alert">
          {AppStrings.PAM_USER_PROMOTIONS_LOAD_ERROR}
        </div>
      ) : isPromotionsView ? (
        <UserPromotionsTab
          promotions={promotionsQuery.data ?? []}
          kpis={promotionsKpisQuery.data ?? EMPTY_USER_PROMOTIONS_KPIS}
          isLoading={isLoading}
          dateFilter={dateFilter}
        />
      ) : (
        <ReferralsTab
          referrals={referralsQuery.data ?? []}
          kpis={referralsKpisQuery.data ?? EMPTY_USER_REFERRALS_KPIS}
          isLoading={isLoading}
          dateFilter={dateFilter}
          currencySymbol={currencySymbol}
        />
      )}
    </section>
  );
}

export default UserPromotions;
